<script setup lang="ts">
/**
 * Aura AGM (Audited · Sole Member — Individual) — AGM Documents.
 *   DRW(이사결의, Audited) + Notice of AGM + Consent to Short Notice(개인 멤버 직접) +
 *   Minutes of AGM(s.184G, 개인 sole member 서명).
 *   법인 버전(s.179(6)/대리인)과 달리 **개인 멤버가 직접 서명**.
 */
import CddVarField from '../cdd/CddVarField.vue';
import AgmSigners from '../cdd/AgmSigners.vue';
import './agmDoc.css';
</script>

<template>
  <div class="agm-doc">
    <!-- ===== DRW ===== -->
    <div class="doc-head">
      <div class="doc-title"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-sub">(Incorporated In Singapore)</div>
      <div class="doc-sub">Company Registration No. <CddVarField field-key="company_registration_no" label="Reg. No." /></div>
      <div class="doc-sub">(the &ldquo;Company&rdquo;)</div>
    </div>
    <div class="sec-title center">DIRECTORS&rsquo; RESOLUTIONS IN WRITING PURSUANT TO THE COMPANY'S CONSTITUTION</div>

    <div class="sec-title">AUDITED FINANCIAL STATEMENTS</div>
    <p class="doc-p">Resolved that the audited financial statements of the Company for the financial year ended
      <CddVarField field-key="financial_year_end" label="FYE" /> together with the directors&rsquo; statement and
      Independent Auditor&rsquo;s Report thereon be approved and that the said directors&rsquo; statement be signed by
      any two directors of the Company on behalf of the board of directors.</p>

    <div class="sec-title">SPECIAL BUSINESS FOR ANNUAL GENERAL MEETING</div>
    <p class="doc-p">Resolved that in addition to the ordinary business of the Annual General Meeting, the following
      resolutions be proposed, by way of a special business, for adoption at the Annual General Meeting.</p>
    <div class="sec-title">ORDINARY RESOLUTIONS</div>
    <div class="sec-title">DIRECTORS&rsquo; FEE AND/OR DIRECTORS&rsquo; REMUNERATION</div>
    <p class="doc-p">That the Directors&rsquo; Fee and/or Directors&rsquo; Remuneration, if any, for the financial year ended
      <CddVarField field-key="financial_year_end" label="FYE" />, as stated in the financial statements be and are hereby approved.</p>
    <div class="sec-title">ANNUAL GENERAL MEETING</div>
    <p class="doc-p">Resolved that any director be authorised to make the necessary arrangements to convene the Annual
      General Meeting of the Company.</p>
    <div class="sec-title">AUTHORITY TO E-FILE RETURNS</div>
    <p class="doc-p">Resolved that any officer of the Company or such other person(s) as directed by the directors be
      authorised to e-file the relevant returns relating thereto with the Accounting and Corporate Regulatory
      Authority, for and on behalf of the Company.</p>
    <div class="doc-meta">Dated: <CddVarField field-key="resolution_date" label="Resolution Date" /></div>
    <div class="sec-title">DIRECTORS / SIGNATURES</div>
    <AgmSigners var-key="agm_Signers" board-label="Directors" />

    <!-- ===== Notice of AGM ===== -->
    <div class="doc-head" style="margin-top:26px">
      <div class="doc-title"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-sub">(Incorporated In Singapore)</div>
      <div class="doc-sub">Company Registration No. <CddVarField field-key="company_registration_no" label="Reg. No." /></div>
      <div class="doc-sub">Registered Office: <CddVarField field-key="registered_office_address" label="Registered Office Address" /></div>
    </div>
    <div class="sec-title center">NOTICE OF ANNUAL GENERAL MEETING</div>
    <p class="doc-p">NOTICE IS HEREBY GIVEN that the Annual General Meeting of the Company will be held at:</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Venue</td><td><CddVarField field-key="registered_office_address" label="Meeting Venue" /></td></tr>
        <tr><td class="cdd-lcell">Date</td><td><CddVarField field-key="meeting_date" label="Meeting Date" /></td></tr>
        <tr><td class="cdd-lcell">Time</td><td><CddVarField field-key="meeting_time" label="Meeting Time" /></td></tr>
      </tbody>
    </table>
    <div class="sec-title">AGENDA</div>
    <p class="doc-p">To receive and adopt the audited financial statements of the Company for the financial year ended
      <CddVarField field-key="financial_year_end" label="FYE" /> together with the directors&rsquo; statement and Independent Auditor&rsquo;s Report thereon.</p>
    <p class="doc-p">To re-elect retiring director.</p>
    <p class="doc-p">To re-appoint auditor and fix their remuneration.</p>
    <p class="doc-p">As special business to propose, and if thought fit, to pass the following ordinary resolutions:-</p>
    <p class="doc-p"><b>Directors&rsquo; Fee and/or Directors&rsquo; Remuneration</b> — That the Directors&rsquo; Fee and/or
      Directors&rsquo; Remuneration, if any, for the financial year ended <CddVarField field-key="financial_year_end" label="FYE" />,
      as stated in the financial statements be and is hereby approved.</p>
    <p class="doc-p">To transact any other business which may properly be transacted at an Annual General Meeting.</p>
    <p class="doc-p">By Order Of The Board</p>
    <div class="sign-line"></div>
    <p class="doc-p"><CddVarField field-key="director_name" label="Director Name" /><br />Director</p>
    <div class="doc-meta">Date: <CddVarField field-key="resolution_date" label="Date" /></div>
    <p class="doc-p"><b>Notes:</b></p>
    <p class="doc-p">A member entitled to attend and vote at the above meeting is entitled to appoint a proxy to attend
      and vote instead of him. A proxy need not be a member of the Company.</p>
    <p class="doc-p">The instrument appointing a proxy must be deposited at the registered office of the Company not less
      than <CddVarField field-key="proxy_cut_off_hours" label="Proxy Cut-off (hours)" /> hours before the meeting.</p>

    <!-- ===== Consent to Short Notice (개인 멤버) ===== -->
    <div class="doc-head" style="margin-top:26px">
      <div class="doc-title"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-sub">Company Registration No. <CddVarField field-key="company_registration_no" label="Reg. No." /></div>
    </div>
    <div class="sec-title center">CONSENT TO SHORT NOTICE OF THE ANNUAL GENERAL MEETING</div>
    <p class="doc-p">I, the undersigned, being the Sole Member of the Company, hereby agree to accept shorter notice of
      the below Annual General Meeting of the Company, notwithstanding that less than full notice as prescribed under
      the Companies Act 1967 and the Constitution of the Company may be given.</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Date</td><td><CddVarField field-key="meeting_date" label="Meeting Date" /></td></tr>
        <tr><td class="cdd-lcell">Time</td><td><CddVarField field-key="meeting_time" label="Meeting Time" /></td></tr>
      </tbody>
    </table>
    <div class="doc-meta">Dated: <CddVarField field-key="resolution_date" label="Date" /></div>
    <div class="sign-line"></div>
    <p class="doc-p"><CddVarField field-key="member_name" label="Sole Member" /><br />Sole Member</p>

    <!-- ===== Minutes of AGM (s.184G, 개인 멤버) ===== -->
    <div class="doc-head" style="margin-top:26px">
      <div class="doc-title"><CddVarField field-key="company_name" label="Company Name" /></div>
      <div class="doc-sub">Company Registration No. <CddVarField field-key="company_registration_no" label="Reg. No." /></div>
    </div>
    <div class="sec-title center">MINUTES OF ANNUAL GENERAL MEETING</div>
    <p class="doc-p">I, the undersigned, being the Sole Member of the Company beneficially entitled to the whole of the
      issued shares of the Company do hereby pass the following resolution pursuant to Section 184G of the Companies
      Act 1967 and agree that the said resolution shall, for all purposes be as valid and effective as if the same had
      been passed at the Annual General Meeting of the Company duly convened and held on
      <CddVarField field-key="meeting_date" label="Meeting Date" />.</p>
    <div class="sec-title">AUDITED FINANCIAL STATEMENTS</div>
    <p class="doc-p">It was resolved that the audited financial statements of the Company for the financial year ended
      <CddVarField field-key="financial_year_end" label="FYE" /> together with the directors&rsquo; statement and
      Independent Auditor&rsquo;s Report thereon be received and adopted.</p>
    <div class="sec-title">DIRECTORS&rsquo; FEE AND/OR DIRECTORS&rsquo; REMUNERATION</div>
    <p class="doc-p">It was resolved that the Directors&rsquo; Fee and/or Directors&rsquo; Remuneration, if any, for the
      financial year ended <CddVarField field-key="financial_year_end" label="FYE" />, as stated in the financial
      statements be and are hereby approved.</p>
    <div class="sec-title">RETIREMENT OF DIRECTOR</div>
    <p class="doc-p">It was resolved that all the Directors who retire in accordance with the Company&rsquo;s Constitution
      were re-elected as Directors of the Company.</p>
    <div class="sec-title">AUDITORS</div>
    <p class="doc-p">It was resolved that <CddVarField field-key="auditors_name" label="Auditors" /> be and are hereby
      re-appointed auditors of the Company to hold office until the conclusion of the next Annual General Meeting at a
      remuneration to be fixed by the directors of the Company.</p>
    <p class="doc-p">Signed as a correct record,</p>
    <div class="sign-line"></div>
    <p class="doc-p"><CddVarField field-key="member_name" label="Sole Member" /><br />Sole Member</p>
  </div>
</template>
